import { createTheme, alpha } from '@mui/material/styles';
import { palette, dark, light, gradients, shadows, radius, fontFamily, transitions, blur } from './designTokens';

type Mode = 'dark' | 'light';
const getTokens = (mode: Mode) => (mode === 'dark' ? dark : light);

export const getTheme = (mode: Mode) => {
  const t = getTokens(mode);
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: { main: palette.primary.main, light: palette.primary.light, dark: palette.primary.dark, contrastText: palette.primary.contrastText },
      secondary: { main: palette.secondary.main, light: palette.secondary.light, dark: palette.secondary.dark, contrastText: palette.secondary.contrastText },
      success: { main: palette.success.main, light: palette.success.light, dark: palette.success.dark },
      warning: { main: palette.warning.main, light: palette.warning.light, dark: palette.warning.dark },
      error: { main: palette.error.main, light: palette.error.light, dark: palette.error.dark },
      info: { main: palette.info.main, light: palette.info.light, dark: palette.info.dark },
      background: { default: t.bg.default, paper: t.bg.paper },
      text: { primary: t.text.primary, secondary: t.text.secondary, disabled: t.text.disabled },
      divider: t.divider,
      action: {
        hover: t.action.hover, selected: t.action.selected, focus: t.action.focus,
        disabledBackground: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.04)', disabled: t.text.disabled,
      },
    },
    typography: {
      fontFamily: fontFamily.primary,
      h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em' },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.01em' },
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0.02em' },
      overline: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.08em', textTransform: 'uppercase' as const },
      button: { fontWeight: 600, textTransform: 'none' as const, letterSpacing: '0.01em' },
    },
    shape: { borderRadius: radius.md },
    components: {
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: {
          backgroundColor: isDark ? 'rgba(11, 15, 26, 0.8)' : 'rgba(248, 250, 252, 0.85)',
          backdropFilter: blur.lg, borderBottom: `1px solid ${t.border.subtle}`, color: t.text.primary,
        }},
      },
      MuiDrawer: { styleOverrides: { paper: {
        backgroundColor: 'transparent', background: isDark ? gradients.sidebar : gradients.sidebarLight,
        borderRight: `1px solid ${t.border.subtle}`,
      }}},
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: {
          borderRadius: radius.xl, border: `1px solid ${t.border.subtle}`,
          backgroundColor: isDark ? t.bg.glass : t.bg.paper, backdropFilter: isDark ? blur.md : 'none',
          backgroundImage: 'none', boxShadow: isDark ? shadows.card : shadows.cardLight,
          transition: `transform ${transitions.normal}, box-shadow ${transitions.normal}`,
          '&:hover': { boxShadow: isDark ? shadows.cardHover : shadows.cardLightHover },
        }},
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: {
          borderRadius: radius.xl, border: `1px solid ${t.border.subtle}`,
          backgroundColor: isDark ? t.bg.glass : t.bg.paper, backdropFilter: isDark ? blur.md : 'none', backgroundImage: 'none',
        }},
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: radius.md, fontWeight: 600, textTransform: 'none' as const, transition: `all ${transitions.normal}` },
          contained: {
            background: gradients.primary, color: '#FFFFFF', boxShadow: shadows.button,
            '&:hover': { background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', boxShadow: shadows.buttonHover, transform: 'translateY(-1px)' },
            '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)' },
          },
          outlined: {
            color: palette.primary.light, borderColor: alpha(palette.primary.main, 0.3), borderWidth: 1.5,
            '&:hover': { backgroundColor: alpha(palette.primary.main, 0.08), borderColor: palette.primary.main, borderWidth: 1.5 },
          },
          text: { color: t.text.secondary, '&:hover': { backgroundColor: t.action.hover, color: t.text.primary } },
          containedSuccess: { background: gradients.success, '&:hover': { background: gradients.success } },
          containedSecondary: { background: gradients.secondary, '&:hover': { background: gradients.secondary } },
        },
      },
      MuiIconButton: { styleOverrides: { root: {
        borderRadius: radius.md, transition: `all ${transitions.fast}`, '&:hover': { backgroundColor: t.action.hover },
      }}},
      MuiOutlinedInput: { styleOverrides: {
        root: {
          borderRadius: radius.md, backgroundColor: isDark ? alpha('#94A3B8', 0.04) : alpha('#0F172A', 0.02),
          transition: `box-shadow ${transitions.normal}`,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border.default, transition: `border-color ${transitions.normal}, box-shadow ${transitions.normal}` },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? alpha('#94A3B8', 0.24) : alpha('#0F172A', 0.2) },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main, borderWidth: 2, boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.15)}` },
        },
        input: { color: t.text.primary },
      }},
      MuiInputLabel: { styleOverrides: { root: { color: t.text.secondary, '&.Mui-focused': { color: palette.primary.light } } } },
      MuiSelect: { styleOverrides: { icon: { color: t.text.secondary } } },
      MuiMenu: { styleOverrides: { paper: {
        backgroundColor: t.bg.elevated, border: `1px solid ${t.border.subtle}`, borderRadius: radius.lg,
        backdropFilter: isDark ? blur.lg : 'none', boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.4)' : '0 16px 40px rgba(0,0,0,0.08)',
      }}},
      MuiMenuItem: { styleOverrides: { root: {
        borderRadius: radius.sm, margin: '2px 6px', transition: `background-color ${transitions.fast}`,
        '&:hover': { backgroundColor: t.action.hover },
        '&.Mui-selected': { backgroundColor: t.action.selected, '&:hover': { backgroundColor: t.action.focus } },
      }}},
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.xs, fontWeight: 600, fontSize: '0.75rem', transition: `all ${transitions.fast}` },
          outlined: { borderWidth: 1.5 },
          colorSuccess: { backgroundColor: palette.success.bg, color: palette.success.light, border: `1px solid ${alpha(palette.success.main, 0.24)}` },
          colorWarning: { backgroundColor: palette.warning.bg, color: palette.warning.light, border: `1px solid ${alpha(palette.warning.main, 0.24)}` },
          colorError: { backgroundColor: palette.error.bg, color: palette.error.light, border: `1px solid ${alpha(palette.error.main, 0.24)}` },
          colorInfo: { backgroundColor: palette.info.bg, color: palette.info.light, border: `1px solid ${alpha(palette.info.main, 0.24)}` },
          colorPrimary: { backgroundColor: alpha(palette.primary.main, 0.12), color: palette.primary.light, border: `1px solid ${alpha(palette.primary.main, 0.2)}` },
          colorSecondary: { backgroundColor: alpha(palette.secondary.main, 0.12), color: palette.secondary.light, border: `1px solid ${alpha(palette.secondary.main, 0.2)}` },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: radius.lg } } },
      MuiTableHead: { styleOverrides: { root: { '& .MuiTableCell-head': {
        backgroundColor: alpha(palette.primary.main, 0.06), color: t.text.secondary, fontWeight: 600, fontSize: '0.75rem',
        textTransform: 'uppercase' as const, letterSpacing: '0.05em', borderBottom: `1px solid ${t.border.subtle}`, whiteSpace: 'nowrap' as const,
      }}}},
      MuiTableBody: { styleOverrides: { root: { '& .MuiTableRow-root': {
        transition: `background-color ${transitions.fast}`,
        '&:nth-of-type(even)': { backgroundColor: isDark ? 'rgba(148, 163, 184, 0.02)' : 'rgba(15, 23, 42, 0.015)' },
        '&:hover': { backgroundColor: alpha(palette.primary.main, 0.04) },
        '& .MuiTableCell-body': { color: t.text.primary, borderBottom: `1px solid ${isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.04)'}`, fontSize: '0.8125rem' },
      }}}},
      MuiTablePagination: { styleOverrides: { root: { color: t.text.secondary, borderTop: `1px solid ${t.border.subtle}` }, selectIcon: { color: t.text.secondary } } },
      MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0', background: gradients.tabIndicator } } },
      MuiTab: { styleOverrides: { root: {
        textTransform: 'none' as const, fontWeight: 500, fontSize: '0.875rem', color: t.text.secondary, minHeight: 48,
        transition: `color ${transitions.fast}`, '&.Mui-selected': { color: t.text.primary, fontWeight: 600 },
      }}},
      MuiDialog: { styleOverrides: { paper: {
        borderRadius: radius.xl, backgroundColor: t.bg.elevated, border: `1px solid ${t.border.subtle}`,
        backdropFilter: isDark ? blur.lg : 'none', boxShadow: shadows.dialog, backgroundImage: 'none',
      }}},
      MuiAccordion: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: {
          backgroundColor: 'transparent', border: `1px solid ${t.border.subtle}`, borderRadius: `${radius.lg}px !important`,
          '&::before': { display: 'none' }, '&.Mui-expanded': { margin: 0, borderColor: alpha(palette.primary.main, 0.16) },
        }},
      },
      MuiLinearProgress: { styleOverrides: {
        root: { height: 8, borderRadius: 4, backgroundColor: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.06)' },
        bar: { borderRadius: 4, background: gradients.primary },
      }},
      MuiCircularProgress: { styleOverrides: { root: { color: palette.primary.main } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius.lg, border: '1px solid', backdropFilter: isDark ? blur.sm : 'none' },
          standardSuccess: { backgroundColor: palette.success.bg, borderColor: alpha(palette.success.main, 0.2), color: palette.success.light },
          standardWarning: { backgroundColor: palette.warning.bg, borderColor: alpha(palette.warning.main, 0.2), color: palette.warning.light },
          standardError: { backgroundColor: palette.error.bg, borderColor: alpha(palette.error.main, 0.2), color: palette.error.light },
          standardInfo: { backgroundColor: palette.info.bg, borderColor: alpha(palette.info.main, 0.2), color: palette.info.light },
        },
      },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.04)' } } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.divider } } },
      MuiTooltip: { styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.9)', color: '#F1F5F9', borderRadius: radius.sm,
          fontSize: '0.75rem', fontWeight: 500, border: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.2)'}`,
          backdropFilter: blur.sm, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', padding: '6px 12px',
        },
        arrow: { color: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.9)' },
      }},
      MuiListItemButton: { styleOverrides: { root: {
        borderRadius: radius.sm, transition: `background-color ${transitions.fast}`,
        '&:hover': { backgroundColor: t.action.hover },
        '&.Mui-selected': { backgroundColor: t.action.selected, '&:hover': { backgroundColor: t.action.focus } },
      }}},
      MuiCssBaseline: { styleOverrides: {
        body: {
          backgroundColor: t.bg.default, color: t.text.primary,
          scrollbarColor: `${isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)'} transparent`, scrollbarWidth: 'thin' as const,
        },
        '::selection': { backgroundColor: alpha(palette.primary.main, 0.3), color: t.text.primary },
      }},
    },
  });
};
